import React, { useRef, useEffect, useState, useMemo } from "react";

const DynamicTextScale = ({
  filteredIndo,
  inx,
  szrgrp,
  valueBasedOnInx,
  referance,
  brand,
  category,
  subCategory,
}) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  // Görsel ayarlar
  const [scale, setScale] = useState(1);
  const [dynamicGap, setDynamicGap] = useState("6px");
  const [dynamicMarginRight, setDynamicMarginRight] = useState(12);
  const [marginLeft, setMarginLeft] = useState("6px");
  const [scaleReady, setScaleReady] = useState(false);

  // Döngü korumaları
  const adjustingRef = useRef(false);
  const lastEffectiveRef = useRef({ container: 0, content: 0 });
  const adjustmentCountRef = useRef(0);

  // Sabitler
  const CONTAINER_MAX_PX = 164; // isteğin
  const SIDE_PADDING_PX = 4;    // tamamen dip dibe olmasın
  const TARGET_INNER_PX = CONTAINER_MAX_PX - SIDE_PADDING_PX * 2;

  const MIN_GAP_PX = 2;         // dip dibe olmasın
  const MIN_MR_PX = 4;          // büyük beden ile sağ blok arasında en az boşluk
  const MIN_ML_PX = 2;          // solda bir nefes
  const MAX_MR_PX = 12;

  const SCALE_MIN = 0.6;        // okunabilirlik için
  const SCALE_EPS = 0.004;      // aynı scale tekrar set edilmesin
  const WIDTH_NOISE = 1.5;      // küçük oynamaları yok say
  const MAX_ADJUSTMENTS = 6;

  // Güvenli veri
  const currentSize = useMemo(
    () => (szrgrp?.sizes?.[valueBasedOnInx] ? szrgrp.sizes[valueBasedOnInx] : {}),
    [szrgrp, valueBasedOnInx]
  );
  const name = filteredIndo?.[inx]?.name;

  const px = (v) => (typeof v === "string" ? parseFloat(v) : v);

  const adjustScale = () => {
    if (adjustingRef.current) return;
    if (adjustmentCountRef.current > MAX_ADJUSTMENTS) {
      // sonsuz uğraşmasın
      return;
    }
    if (!containerRef.current || !contentRef.current) return;

    // container genişliğini (ekranda görünen) ve content'in efektif genişliğini ölç
    const containerEff = containerRef.current.getBoundingClientRect().width;
    const contentEff = contentRef.current.getBoundingClientRect().width;
    const last = lastEffectiveRef.current;

    // çok küçük farklarda uğraşma
    if (
      Math.abs(containerEff - last.container) < WIDTH_NOISE &&
      Math.abs(contentEff - last.content) < WIDTH_NOISE
    ) {
      return;
    }

    adjustingRef.current = true;
    adjustmentCountRef.current += 1;

    requestAnimationFrame(() => {
      // Başlangıç değerleri
      let newGapPx = px(dynamicGap ?? "6px");
      let newMR = dynamicMarginRight ?? MAX_MR_PX;
      let newML = px(marginLeft ?? `${MIN_ML_PX}px`);
      let newScale = scale ?? 1;

      // Bedene göre “başlangıç” boşluk önerisi
      if (name === "S") {
        newGapPx = 8;
        newML = 10;
      } else if (name === "M") {
        newGapPx = 6;
        newML = 6;
      } else if (name === "L") {
        newGapPx = 9;
        newML = 13;
      } else if (name === "XXL") {
        newGapPx = 4;
        newML = 2;
      }

      // Doğal genişlik (transform’dan etkilenmez)
      const naturalContentWidth = contentRef.current.scrollWidth;

      // İç boşlukların toplam etkisini yaklaşıkla:
      // - 3 küçük kolon arasında 2 gap var
      // - büyük beden ile kolon grubu arasında marginRight var
      // Bu yüzden gap'i kısınca 2*delta kadar, MR'ı kısınca delta kadar doğal genişlik düşer.
      const currentGapPx = px(dynamicGap ?? "6px");
      const currentMR = dynamicMarginRight ?? MAX_MR_PX;

      const reduceToFit = () => {
        const initialNatural = naturalContentWidth;

        // 1) Önce gap'i MIN_GAP_PX'e kadar indir
        let testGap = Math.max(MIN_GAP_PX, Math.min(currentGapPx, newGapPx));
        // 2) MR'ı MIN_MR_PX'e kadar indir
        let testMR = Math.max(MIN_MR_PX, Math.min(currentMR, newMR));

        // Gap/MR düşüşünün yaklaşık etkisi:
        const approxNaturalAfterSpacing =
          initialNatural -
          (currentGapPx - testGap) * 2 -
          (currentMR - testMR);

        if (approxNaturalAfterSpacing <= TARGET_INNER_PX) {
          // Sadece spacing ile sığdık
          return { gap: testGap, mr: testMR, scale: 1 };
        }

        // 3) Hâlâ büyükse scale uygula (spacing sonrası)
        const neededScale = Math.min(
          1,
          Math.max(
            SCALE_MIN,
            TARGET_INNER_PX / Math.max(approxNaturalAfterSpacing, 1)
          )
        );

        return { gap: testGap, mr: testMR, scale: neededScale };
      };

      const res = reduceToFit();
      newGapPx = res.gap;
      newMR = res.mr;
      newScale = res.scale;

      // State'leri sadece gerçekten değiştiyse set et
      if (Math.abs(newScale - scale) > SCALE_EPS) setScale(newScale);
      if (`${newGapPx}px` !== dynamicGap) setDynamicGap(`${newGapPx}px`);
      if (newMR !== dynamicMarginRight) setDynamicMarginRight(newMR);
      if (`${Math.max(newML, MIN_ML_PX)}px` !== marginLeft)
        setMarginLeft(`${Math.max(newML, MIN_ML_PX)}px`);

      setScaleReady(true);

      // Son efektif ölçümleri kaydet
      lastEffectiveRef.current = {
        container: containerRef.current.getBoundingClientRect().width,
        content: contentRef.current.getBoundingClientRect().width,
      };

      setTimeout(() => {
        adjustingRef.current = false;
      }, 100);
    });
  };

  useEffect(() => {
    // İlk ayar
    const t = setTimeout(adjustScale, 150);

    // Resize (debounce)
    let rTO;
    const onResize = () => {
      if (adjustingRef.current) return;
      clearTimeout(rTO);
      rTO = setTimeout(adjustScale, 160);
    };
    window.addEventListener("resize", onResize);

    // ResizeObserver (container + content)
    let oTO;
    const ro = new ResizeObserver(() => {
      if (adjustingRef.current) return;
      clearTimeout(oTO);
      oTO = setTimeout(adjustScale, 140);
    });
    if (containerRef.current) ro.observe(containerRef.current);
    if (contentRef.current) ro.observe(contentRef.current);

    return () => {
      clearTimeout(t);
      clearTimeout(rTO);
      clearTimeout(oTO);
      window.removeEventListener("resize", onResize);
      try {
        if (containerRef.current) ro.unobserve(containerRef.current);
        if (contentRef.current) ro.unobserve(contentRef.current);
      } catch {}
    };
  }, [filteredIndo, inx]);

  return (
    <div
      style={{
        position: "relative",
        top: 108,
        display: "flex",
        left: 11,
        justifyContent: "center",
        alignItems: "center",
        width: `${CONTAINER_MAX_PX}px`, // mutlak 164 px
        margin: "0 auto",
        padding: `${SIDE_PADDING_PX}px`, // kenarlarda nefes
        boxSizing: "border-box",
      }}
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginTop:-4,
          transform: "translateX(0px)",
        }}
      >
        <div
          ref={contentRef}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "nowrap",
            transform: `scaleX(${scale})`,
            transition: "transform 0.25s ease, opacity 0.25s ease",
            opacity: scaleReady ? 1 : 0,
            // Not: scrollWidth hesapları transform'dan etkilenmediği için sorun yok
          }}
        >
          {/* Büyük beden */}
          <p
            className="arial-regular"
            style={{
              fontSize: "20pt",
              margin: 0,
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              fontWeight: 900,
              overflow: "hidden",
              marginRight: dynamicMarginRight,
              marginTop: 10,
              marginLeft: marginLeft,
            }}
          >
            {currentSize?.europe ?? "N/A"}
          </p>

          {/* Diğer bedenler */}
          <div
            style={{
              display: "flex",
              whiteSpace: "nowrap",
              overflow: "hidden",
              gap: dynamicGap,
            }}
          >
            {[
              { label: "MEX:", value: currentSize?.mexico },
              { label: "USA:", value: currentSize?.usa },
              { label: "BR:", value: currentSize?.["br-brasil"] },
            ].filter(Boolean).map((item, index) => (
              <div
                key={`${item.label}-${index}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                <p
                  className="pdf-force"
                  style={{ fontSize: "5pt", marginTop: "12px",fontWeight:500 }}
                >
                  {item.label}
                </p>
                <p
                  className="arial-regular"
                  style={{ fontSize: "11pt", marginTop:-4,  fontWeight: 900 }}
                >
                  {item.value ?? "—"}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicTextScale;
